import React, { useState, useContext } from 'react';
import styled from '@emotion/styled';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import type { RepDashboardWidgetConfig } from '@trinity/chat/types/dashboard-config';
import type { RepDashboardWidgetData, RepDashboardKpiItem, RepDashboardTableRow, RepDashboardRelatedQuestion } from '@trinity/chat/types/dashboard-data';
import { RepDashboardProfileClickContext } from './RepDashboardProfileClickContext';
import { RepDashboardSubmitQueryContext } from './RepDashboardSubmitQueryContext';

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  data: RepDashboardWidgetData;
  config: RepDashboardWidgetConfig;
};

// ─── Main Component ─────────────────────────────────────────────────────────

export function RepDashboardWidget({ data, config }: Props) {
  const onProfileClick = useContext(RepDashboardProfileClickContext);
  const visible = config.visibleSections ?? {};
  const showSummary = visible.summary !== false;
  const showKpis = visible.kpis !== false;
  const showOpportunities = visible.opportunities !== false;
  const showTable = visible.table !== false;

  const headerColor = config.headerColor ?? '#001845';
  const accentColor = config.accentColor ?? '#FF5757';

  return (
    <Container>
      {showSummary && (
        <SummaryBanner summaryText={data.summaryText} accentColor={accentColor} />
      )}
      {showKpis && data.kpis.length > 0 && (
        <CollapsibleSection title="Performance and KPI Summary" headerColor={headerColor}>
          <KPIGrid kpis={data.kpis} accentColor={accentColor} />
        </CollapsibleSection>
      )}
      {showOpportunities && data.opportunities.length > 0 && (
        <CollapsibleSection title="Potential Opportunities" headerColor={headerColor}>
          <OpportunitiesContent opportunities={data.opportunities} infoBoxText={data.infoBoxText} />
        </CollapsibleSection>
      )}
      {showTable && data.tableRows.length > 0 && (
        <TableSection>
          {data.tableInfo && <TableInfoText>{data.tableInfo}</TableInfoText>}
          <TableCard>
            <HCPTable rows={data.tableRows} defaultRowsPerPage={config.defaultRowsPerPage ?? 5} />
          </TableCard>
        </TableSection>
      )}
      {data.relatedQuestions && data.relatedQuestions.length > 0 && (
        <RelatedQuestions questions={data.relatedQuestions} />
      )}
    </Container>
  );
}

// ─── Summary Banner ─────────────────────────────────────────────────────────

function SummaryBanner({ summaryText, accentColor }: { summaryText: string; accentColor: string }) {
  return (
    <BannerWrap>
      <IconCircle style={{ backgroundColor: accentColor }}>
        <LightbulbIcon sx={{ fontSize: 20, color: '#fff' }} />
      </IconCircle>
      <BannerText>{summaryText}</BannerText>
    </BannerWrap>
  );
}

// ─── Collapsible Section ────────────────────────────────────────────────────

function CollapsibleSection({
  title,
  headerColor,
  children,
}: {
  title: string;
  headerColor: string;
  children: React.ReactNode;
}) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <SectionCard>
      <SectionHeader onClick={() => setIsExpanded(!isExpanded)} style={{ backgroundColor: headerColor }}>
        <ExpandIcon sx={{ fontSize: 20, transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
        <SectionTitle>{title}</SectionTitle>
      </SectionHeader>
      {isExpanded && <SectionBody>{children}</SectionBody>}
    </SectionCard>
  );
}

// ─── KPI Grid ───────────────────────────────────────────────────────────────

function KPIGrid({ kpis, accentColor }: { kpis: RepDashboardKpiItem[]; accentColor: string }) {
  return (
    <KPIGridWrap count={kpis.length}>
      {kpis.map((kpi, i) => (
        <KPICardItem key={i} kpi={kpi} accentColor={accentColor} />
      ))}
    </KPIGridWrap>
  );
}

function KPICardItem({ kpi, accentColor }: { kpi: RepDashboardKpiItem; accentColor: string }) {
  return (
    <KPICardWrap>
      <KPILabel>{kpi.label}</KPILabel>
      <KPIValueRow>
        <KPIValue style={{ color: accentColor }}>{kpi.value}</KPIValue>
        <KPIUnit>{kpi.unit}</KPIUnit>
        {kpi.subValue && (
          <>
            <KPISeparator>|</KPISeparator>
            <KPISubValue>{kpi.subValue}</KPISubValue>
          </>
        )}
      </KPIValueRow>
      <KPIChangeRow>
        <ChangeBadge changeType={kpi.changeType}>{kpi.change}</ChangeBadge>
        <ChangeLabel>{kpi.changeLabel}</ChangeLabel>
      </KPIChangeRow>
    </KPICardWrap>
  );
}

// ─── Opportunities ──────────────────────────────────────────────────────────

function OpportunitiesContent({
  opportunities,
  infoBoxText,
}: {
  opportunities: string[];
  infoBoxText?: string;
}) {
  return (
    <OpportunitiesWrap>
      <OpportunityList>
        {opportunities.map((text, i) => (
          <OpportunityItem key={i}>
            <Bullet>&bull;</Bullet>
            <span>{text}</span>
          </OpportunityItem>
        ))}
      </OpportunityList>
      {infoBoxText && (
        <InfoBox>
          <InfoBoxText>{infoBoxText}</InfoBoxText>
        </InfoBox>
      )}
    </OpportunitiesWrap>
  );
}

// ─── HCP Table ──────────────────────────────────────────────────────────────

function HCPTable({ rows, defaultRowsPerPage }: { rows: RepDashboardTableRow[]; defaultRowsPerPage: number }) {
  const onProfileClick = useContext(RepDashboardProfileClickContext);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(rows.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, rows.length);
  const currentData = rows.slice(startIndex, endIndex);

  return (
    <TableWrap>
      <TableContainer>
        <StyledTable>
          <thead>
            <tr>
              <Th>HCP ID</Th>
              <Th>Profile</Th>
              <Th>HCP Name</Th>
              <Th>Address</Th>
              <Th>Call Goal</Th>
              <Th>Remaining Calls</Th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((row) => (
              <Tr key={row.id}>
                <Td>{row.id}</Td>
                <Td>
                  {row.profileId && (
                    <ProfileButton onClick={() => onProfileClick?.(row.profileId!, row.name)}>
                      <ArrowForwardIcon sx={{ fontSize: 16 }} />
                    </ProfileButton>
                  )}
                </Td>
                <TdBold>{row.name}</TdBold>
                <Td>{row.address}</Td>
                <TdBold>{row.callGoal}</TdBold>
                <TdBold>{row.remainingCalls}</TdBold>
              </Tr>
            ))}
          </tbody>
        </StyledTable>
      </TableContainer>

      <PaginationWrap>
        <PaginationLeft>
          <PaginationLabel>Rows per page:</PaginationLabel>
          <PaginationSelect
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
          </PaginationSelect>
        </PaginationLeft>
        <PaginationRight>
          <PaginationLabel>
            {startIndex + 1}-{endIndex} of {rows.length}
          </PaginationLabel>
          <PaginationNav>
            <PaginationButton
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeftIcon sx={{ fontSize: 16 }} />
            </PaginationButton>
            <PaginationButton
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            </PaginationButton>
          </PaginationNav>
        </PaginationRight>
      </PaginationWrap>
    </TableWrap>
  );
}

// ─── Related Questions ───────────────────────────────────────────────────────

function RelatedQuestions({ questions }: { questions: RepDashboardRelatedQuestion[] }) {
  const onSubmitQuery = useContext(RepDashboardSubmitQueryContext);

  return (
    <RelatedQuestionsWrap>
      <RelatedQuestionsTitle>Related Questions</RelatedQuestionsTitle>
      <RelatedQuestionsList>
        {questions.map((q, i) => (
          <RelatedQuestionButton key={i} onClick={() => onSubmitQuery?.(q.text)}>
            <ArrowForwardIcon sx={{ fontSize: 16, color: '#1E3A5F', flexShrink: 0 }} />
            <span>{q.text}</span>
          </RelatedQuestionButton>
        ))}
      </RelatedQuestionsList>
    </RelatedQuestionsWrap>
  );
}

// ─── Styled Components ──────────────────────────────────────────────────────

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  overflowY: 'auto',
});

// Banner
const BannerWrap = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '0 8px',
});

const IconCircle = styled.div({
  width: 40,
  height: 40,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const BannerText = styled.span(({ theme }) => ({
  fontSize: 20,
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

// Collapsible section
const SectionCard = styled.div(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  overflow: 'hidden',
}));

const SectionHeader = styled.button({
  width: '100%',
  border: 'none',
  color: '#fff',
  padding: '16px 24px',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  cursor: 'pointer',
  transition: 'background-color 0.15s',
  '&:hover': { filter: 'brightness(1.1)' },
});

const ExpandIcon = styled(ExpandMoreIcon)({
  color: '#fff',
  transition: 'transform 0.2s',
});

const SectionTitle = styled.span({
  fontSize: 18,
  fontWeight: 400,
});

const SectionBody = styled.div({
  padding: 32,
});

// KPI grid
const KPIGridWrap = styled.div<{ count: number }>(({ count }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${Math.min(count, 5)}, 1fr)`,
  gap: 32,
  '@media (max-width: 960px)': {
    gridTemplateColumns: `repeat(${Math.min(count, 3)}, 1fr)`,
  },
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
  },
}));

const KPICardWrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const KPILabel = styled.div({
  fontSize: 12,
});

const KPIValueRow = styled.div({
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
  flexWrap: 'wrap',
});

const KPIValue = styled.span({
  fontSize: 30,
  fontWeight: 400,
});

const KPIUnit = styled.span({
  fontSize: 12,
});

const KPISeparator = styled.span(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.divider,
  margin: '0 4px',
}));

const KPISubValue = styled.span(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.primary,
}));

const KPIChangeRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
});

const ChangeBadge = styled.span<{ changeType: 'positive' | 'negative' | 'neutral' }>(({ changeType }) => ({
  fontSize: 12,
  padding: '2px 8px',
  borderRadius: 4,
  fontWeight: 500,
  ...(changeType === 'positive' && { color: '#15803d', backgroundColor: '#f0fdf4' }),
  ...(changeType === 'negative' && { color: '#b91c1c', backgroundColor: '#fef2f2' }),
  ...(changeType === 'neutral' && { color: '#374151', backgroundColor: '#f3f4f6' }),
}));

const ChangeLabel = styled.span({
  fontSize: 12,
});

// Opportunities
const OpportunitiesWrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
});

const OpportunityList = styled.ul({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const OpportunityItem = styled.li({
  display: 'flex',
  gap: 12,
  lineHeight: 1.6,
  fontSize: 14,
});

const Bullet = styled.span(({ theme }) => ({
  color: theme.palette.text.disabled,
  marginTop: 2,
  flexShrink: 0,
}));

const InfoBox = styled.div({
  backgroundColor: '#E8F4FD',
  border: '1px solid #B3D9F2',
  borderRadius: 8,
  padding: 16,
});

const InfoBoxText = styled.p({
  margin: 0,
  color: '#001845',
  fontSize: 14,
});

// Table
const TableCard = styled.div(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  overflow: 'hidden',
  padding: 32,
}));

const TableWrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const TableContainer = styled.div(({ theme }) => ({
  overflowX: 'auto',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
}));

const StyledTable = styled.table({
  width: '100%',
  borderCollapse: 'collapse',
});

const Th = styled.th(({ theme }) => ({
  textAlign: 'left',
  padding: '12px 16px',
  fontSize: 12,
  fontWeight: 600,
  backgroundColor: theme.palette.action.hover,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const Tr = styled.tr(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:hover': { backgroundColor: theme.palette.action.hover },
  '&:last-child': { borderBottom: 'none' },
}));

const Td = styled.td(({ theme }) => ({
  padding: '12px 16px',
  fontSize: 14,
  color: theme.palette.text.secondary,
}));

const TdBold = styled.td(({ theme }) => ({
  padding: '12px 16px',
  fontSize: 14,
  color: theme.palette.text.primary,
}));

const ProfileButton = styled.button({
  background: 'none',
  border: 'none',
  color: '#5B4FE5',
  cursor: 'pointer',
  padding: 4,
  display: 'flex',
  alignItems: 'center',
  borderRadius: 4,
  '&:hover': { color: '#4a3fd4', backgroundColor: 'rgba(91, 79, 229, 0.08)' },
});

// Pagination
const PaginationWrap = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: 14,
});

const PaginationLeft = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const PaginationRight = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});

const PaginationLabel = styled.span(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
}));

const PaginationSelect = styled.select(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 4,
  padding: '4px 8px',
  fontSize: 14,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
}));

const PaginationNav = styled.div({
  display: 'flex',
  gap: 4,
});

const PaginationButton = styled.button(({ theme }) => ({
  padding: 4,
  borderRadius: 4,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  '&:hover:not(:disabled)': { backgroundColor: theme.palette.action.hover },
  '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
}));

// Table info + table grouped tightly
const TableSection = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
});

const TableInfoText = styled.p({
  margin: 0,
  padding: '0 32px',
  fontSize: 14,
  lineHeight: 1.6,
});

// Related questions
const RelatedQuestionsWrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

const RelatedQuestionsTitle = styled.h3(({ theme }) => ({
  margin: 0,
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const RelatedQuestionsList = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const RelatedQuestionButton = styled.button({
  display: 'inline-flex',
  alignItems: 'center',
  alignSelf: 'flex-start',
  gap: 12,
  padding: '10px 20px',
  border: '1px solid #E2E8F0',
  borderRadius: 8,
  backgroundColor: '#F8FAFC',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  color: '#1E3A5F',
  fontSize: 14,
  textAlign: 'left',
  cursor: 'pointer',
  transition: 'background-color 0.15s',
  '&:hover': { backgroundColor: '#EDF2F7' },
});
